// Voluntary exploration follows the real map. Old discoveries remain claimable,
// but new explorers have no preset destination or scripted reward run.
import { EXPEDITIONS, RELICS, RELIC_MAX } from '../../shared/realm'
import type { ClientMsg } from '../../shared/protocol'
import type { UnitWire } from '../../shared/types'
import { el, fmtCost, unitActivity, type UICtx } from './panels'
import { discoveryList } from './campProject'

export function initAdventures({ st, tx, root, jumpTo }: UICtx): void {
  const launch = el('button', 'panel adventure-launch exploration-launch hidden')
  launch.setAttribute('aria-label', 'Explore the map')
  launch.setAttribute('aria-expanded', 'false')
  launch.setAttribute('aria-controls', 'exploration-panel')
  const back = el('div', 'exploration-back hidden')
  const panel = el('section', 'panel exploration-panel')
  panel.id = 'exploration-panel'
  panel.setAttribute('aria-labelledby', 'exploration-title')
  const head = el('div', 'journal-head')
  const title = el('h2', '', 'Explore the map'); title.id = 'exploration-title'
  const close = el('button', 'btn btn-ghost', '×')
  close.setAttribute('aria-label', 'Close exploration')
  head.append(title, close)
  const body = el('div', 'exploration-body')
  const error = el('p', 'journal-error hidden'); error.setAttribute('role', 'alert')
  panel.append(head, body, error); back.append(panel); root.append(launch, back)
  let open = false, signature = '', legacyOpen = false
  let seenFor: string | null = null, seenDiscoveryTick = -1
  let pending: { key: string; at: number; settled: () => boolean } | null = null

  const explorers = (): UnitWire[] => [...st.units.values()].filter(u =>
    u.owner === st.me && u.type === 'citizen' && !u.hauler && u.hp > 0 && u.exploring)
  function setOpen(value: boolean, focus = true): void {
    open = value
    back.classList.toggle('hidden', !value)
    launch.setAttribute('aria-expanded', String(value))
    signature = ''; refresh()
    if (focus) (value ? close : launch).focus({ preventScroll: true })
  }
  launch.onclick = () => setOpen(!open)
  close.onclick = () => setOpen(false)
  // Existing entry points can still open an already-earned discovery.
  st.on('openAdventures', () => setOpen(true))
  st.on('closeAdventures', () => setOpen(false))
  st.on('openExploration', () => setOpen(true))

  function dispatch(key: string, message: ClientMsg, settled: () => boolean): void {
    if (pending || !st.connected || !st.me || st.observer || st.self?.ruined) return
    const request = { key, at: Date.now(), settled }
    pending = request
    error.classList.add('hidden')
    tx(message)
    signature = ''; refresh()
    setTimeout(() => {
      if (pending !== request) return
      pending = null; signature = ''; refresh()
    }, 4000)
  }
  function action(label: string, key: string, run: () => void, disabled = false): HTMLButtonElement {
    const button = el('button', 'btn', label)
    button.dataset.exploreAction = key
    button.disabled = disabled || !st.connected || !!pending
    button.onclick = run
    return button
  }
  function refresh(): void {
    const visible = !!st.me && !!st.self && !st.observer && !st.self?.ruined
    launch.classList.toggle('hidden', !visible)
    if (!visible) {
      back.classList.add('hidden'); launch.setAttribute('aria-expanded', 'false')
      open = false; pending = null; return
    }
    if (pending && (pending.settled() || Date.now() - pending.at >= 4000)) pending = null
    const people = explorers(), p = st.self?.realm, discoveries = st.self?.camp?.discoveries ?? []
    if (seenFor !== st.me) {
      seenFor = st.me; seenDiscoveryTick = -1
      try {
        const saved = localStorage.getItem(`hexDiscoveriesSeen:${st.me}`)
        if (saved !== null && Number.isFinite(Number(saved))) seenDiscoveryTick = Number(saved)
      } catch { /* the in-session badge still works when storage is unavailable */ }
    }
    const latest = discoveries.reduce<typeof discoveries[number] | undefined>((best, find) => !best || find.tick > best.tick ? find : best, undefined)
    if (open && latest && latest.tick > seenDiscoveryTick) {
      seenDiscoveryTick = latest.tick
      try { localStorage.setItem(`hexDiscoveriesSeen:${st.me}`, String(seenDiscoveryTick)) } catch { /* session-only acknowledgement */ }
    }
    const e = p?.expedition, find = p?.discovery
    launch.replaceChildren(el('strong', '', '🧭 Explore the map'), el('span', '',
      latest && latest.tick > seenDiscoveryTick ? `${latest.by} found ${latest.name}`
        : people.length ? `${people.length} villager${people.length === 1 ? '' : 's'} exploring${discoveries.length ? ` · ${discoveries.length} place${discoveries.length === 1 ? '' : 's'} found` : ''}`
          : discoveries.length ? `${discoveries.length} useful place${discoveries.length === 1 ? '' : 's'} found · view the map` : 'Send someone to uncover useful places'))
    launch.classList.toggle('ready', !!latest && latest.tick > seenDiscoveryTick)
    if (!open) return
    const key = JSON.stringify([people.map(u => [u.id, u.person?.name]), e && [e.serial, e.partyLeft, e.guardsLeft],
      find, p?.relics, discoveries, st.connected, pending?.key])
    if (key !== signature) {
      signature = key
      const focused = (document.activeElement as HTMLElement)?.dataset.exploreAction
      const scroll = body.scrollTop
      body.replaceChildren()
      body.append(el('p', 'exploration-intro', 'Villagers wander to reveal terrain and report useful places. Their discoveries stay marked on your map.'))
      const send = action(pending?.key === 'send' ? 'Sending a villager…' : people.length ? 'Send another free villager' : 'Send a free villager exploring', 'send', () => {
        const before = new Set(explorers().map(u => u.id))
        dispatch('send', { t: 'explore', ids: [], on: true }, () => explorers().some(u => !before.has(u.id)))
      })
      send.classList.add('btn-gold', 'exploration-send')
      if (people.length) {
        for (const u of people) {
          const row = el('article', 'explorer-row'); row.dataset.explorer = String(u.id)
          row.append(el('strong', 'explorer-name', u.person?.name || 'Villager'), el('span', 'explorer-status'))
          const controls = el('div', 'explorer-actions')
          const follow = el('button', 'btn btn-sm', 'Follow'); follow.dataset.exploreAction = `follow-${u.id}`
          follow.onclick = () => {
            const live = st.units.get(u.id)
            if (live) { jumpTo(live.q, live.r); setOpen(false) }
          }
          controls.append(follow, action('Return to village', `return-${u.id}`, () => dispatch(`return-${u.id}`,
            { t: 'explore', ids: [u.id], on: false }, () => !st.units.get(u.id)?.exploring)))
          row.append(controls); body.append(row)
        }
        if (people.length > 1) body.append(action('Recall all explorers', 'recall-all', () => dispatch('recall-all',
          { t: 'explore', ids: [], on: false }, () => explorers().length === 0)))
      }
      body.append(send, el('p', 'exploration-note', 'One free villager · no fee. Choose a specific person from their panel.'))
      if (!people.length) body.append(el('p', 'exploration-empty', 'Nobody exploring. Set out whenever you’re ready.'))
      if (discoveries.length) {
        body.append(el('h3', 'camp-discoveries-title', 'Places we found'), discoveryList(discoveries, discovery => {
          jumpTo(discovery.q, discovery.r); setOpen(false)
        }, 'exploreAction'))
      }
      // Compatibility only: old saves can recall their party or resolve a find.
      // There are deliberately no new expedition dispatch buttons or map pins.
      if (p && (e || find)) {
        const legacy = el('details', 'exploration-legacy')
        legacy.open = legacyOpen
        legacy.addEventListener('toggle', () => { legacyOpen = legacy.open })
        legacy.append(el('summary', '', find ? 'Earlier journey · unclaimed discovery' : 'Earlier journey · party still traveling'))
        if (find) {
          const def = RELICS[find.kind], level = p.relics?.[find.kind] ?? 0
          legacy.append(el('p', '', `${def.name} — ${def.story}`))
          legacy.append(action(`Keep relic · ${def.bonus}`, 'legacy-keep', () => dispatch('legacy-keep',
            { t: 'discovery', serial: find.serial, choice: 'keep' }, () => !st.self?.realm?.discovery), level >= RELIC_MAX))
          legacy.append(action(`Take supplies · ${fmtCost(def.salvage)}`, 'legacy-salvage', () => dispatch('legacy-salvage',
            { t: 'discovery', serial: find.serial, choice: 'salvage' }, () => !st.self?.realm?.discovery)))
        }
        if (e) {
          legacy.append(el('p', 'legacy-party-status', `${EXPEDITIONS[e.kind].name} · ${e.partyLeft} villagers traveling`))
          const follow = el('button', 'btn', 'Follow earlier party'); follow.dataset.exploreAction = 'legacy-follow'
          follow.onclick = () => {
            const live = e.party.map(id => st.units.get(id)).find(Boolean)
            jumpTo(live?.q ?? e.q, live?.r ?? e.r); setOpen(false)
          }
          legacy.append(follow, action('Recall earlier party', 'legacy-recall', () => dispatch('legacy-recall',
            { t: 'recallExpedition' }, () => !st.self?.realm?.expedition)))
        }
        body.append(legacy)
      }
      body.scrollTop = scroll
      if (focused) (body.querySelector<HTMLButtonElement>(`[data-explore-action="${focused}"]:not(:disabled)`) ?? close).focus({ preventScroll: true })
    }
    for (const row of body.querySelectorAll<HTMLElement>('[data-explorer]')) {
      const u = st.units.get(Number(row.dataset.explorer)), status = row.querySelector('.explorer-status')
      if (u && status) status.textContent = `${unitActivity(st, u)} · ${u.q}, ${u.r}`
    }
    const legacyStatus = body.querySelector('.legacy-party-status')
    if (e && legacyStatus) legacyStatus.textContent = `${EXPEDITIONS[e.kind].name} · ${e.partyLeft} villagers · ${e.guardsLeft ? `${e.guardsLeft} guards` : `${e.distanceLeft ?? '?'} hexes remaining`}`
  }
  for (const event of ['self', 'tick', 'welcome', 'connection']) st.on(event, refresh)
  st.on('error', message => {
    const requested = !!pending
    pending = null; signature = ''; refresh()
    if (open && requested) { error.textContent = String(message); error.classList.remove('hidden') }
  })
  window.addEventListener('keydown', event => {
    if (open && event.key === 'Escape') {
      event.preventDefault(); event.stopImmediatePropagation(); setOpen(false)
    }
  }, true)
  // The small panel never captures map input outside its own bounds.
  window.addEventListener('pointerdown', event => {
    if (open && event.target instanceof Node && !panel.contains(event.target) && !launch.contains(event.target)) setOpen(false, false)
  })
}
